// react
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// utils
import { reportInviteError } from 'utils/inviteError';

// components
import IFAPortalPage from '@karehero/llama/components/pages/IFAPortal/IFAPortal';
import { theme } from '@karehero/llama/theme';

import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

// store
import {
  useGetOrganizationAccountsQuery,
  useGetInviteEmailTemplateQuery,
  useGetSessionOrganizationQuery,
  useReinviteMemberMutation,
} from 'store/api/organization';
import { useLazyGetAllCareRecipientSummaryByAccountQuery } from 'store/api/careCircle';
import { useSendInternalReferralMutation } from 'store/api/referral';
import { newToast } from 'store/slices/uiSlice';
import { getAccount } from 'store/slices/iamSlice';
import { useSelector } from 'store';

// hooks
import { useCareProfileForm } from 'hooks';

// tracking
import { TrackingEvent, useTracking, useWindowSize } from 'hooks';

// models
import {
  CareProfileCategoryID,
  CareRecipientSummary,
  isIFAOrganizationTypeID,
  ReferClientRequest,
} from '@karehero/models';

// posthog
import { useFeatureFlags } from 'hooks/useFeatureFlags';

/**
 * IFAPortal shows the accounts and invited members of an organization
 */
export const IFAPortal = () => {
  // stages
  const [selectedCareRecipient, setSelectedCareRecipient] =
    useState<CareRecipientSummary>({} as CareRecipientSummary);

  // hooks
  const dispatch = useDispatch();
  const { data: organization } = useGetSessionOrganizationQuery();
  const { data: accounts } = useGetOrganizationAccountsQuery(
    { organizationID: organization?.id || '', role: 'member' },
    { skip: !organization?.id },
  );
  const { data: template } = useGetInviteEmailTemplateQuery();
  const [sendInternalReferral] = useSendInternalReferralMutation();
  const [reinviteMember] = useReinviteMemberMutation();
  const account = useSelector(getAccount);
  const { handlePrintPdf } = useCareProfileForm({
    categoryID: CareProfileCategoryID.FinancialStatus,
  });

  const [careRecipientSummaries] =
    useLazyGetAllCareRecipientSummaryByAccountQuery();

  const [width] = useWindowSize();
  const { track } = useTracking();
  const { flags } = useFeatureFlags();

  // effects
  useEffect(() => {
    track(TrackingEvent.InternalPartnerReferralFormViewed, {});
  }, [track]);

  // callbacks
  const inviteClient = useCallback(
    (formData: ReferClientRequest) => {
      sendInternalReferral(formData);
      track(TrackingEvent.InternalPartnerReferralFormSubmitted, {});
    },
    [track, sendInternalReferral],
  );

  // methods
  const handleResendInvite = useCallback(
    async (id?: string, email?: string) => {
      if (!id) return;
      try {
        await reinviteMember(id).unwrap();
        dispatch(
          newToast({
            title: 'Invitation Success',
            description: `Resent invitation to ${email || 'the user'}`,
          }),
        );
      } catch (e) {
        reportInviteError(e);
      }
    },
    [dispatch, reinviteMember],
  );

  const getCareRecipientShortlistData = useCallback(
    async (id: string) => {
      const res = await careRecipientSummaries(id || '');
      if (res.data) {
        setSelectedCareRecipient(res.data[0]);
        return res.data;
      }
      return [];
    },
    [careRecipientSummaries, setSelectedCareRecipient],
  );

  const isIFA = isIFAOrganizationTypeID(organization?.organizationTypeId);

  const onPrintPdf = useCallback(() => {
    handlePrintPdf(
      selectedCareRecipient.careRecipientFinancialAssessmentAnswers,
      selectedCareRecipient.fullName,
      true,
    );
  }, [handlePrintPdf, selectedCareRecipient]);

  return (
    <PageTemplate
      maxWidth='xl'
      headerSlot={
        <PageHeader title={`${organization?.name || ''} Invite Portal`} />
      }
      contentSlot={
        <IFAPortalPage
          isLoading={!accounts || !account || !organization || !template}
          accounts={accounts}
          ownerAccount={account}
          organization={organization}
          emailTemplate={template || ''}
          inviteClient={inviteClient}
          resendInvite={handleResendInvite}
          notifyToast={(req: { title: string; description: string }) =>
            dispatch(newToast(req))
          }
          getCareRecipientShortlistData={getCareRecipientShortlistData}
          isMobile={width < theme.breakpoint.mdValue}
          isShortlistSummary={!!flags.shortlistSummary}
          onPrintPdf={onPrintPdf}
          isTitleHidden
        />
      }
    />
  );
};

export default IFAPortal;
